<template>
  <div>
    <div :class="containerClass" class="d-flex justify-content-between mt-3">
      <h2>
        Assessment Submissions
        <i class="material-icons icon-16pt clickable-item" v-b-popover.hover.top="'View student assessment submissions and attempts.'">
          info
        </i>
      </h2>
    </div>

    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center mb-3" style="white-space: nowrap">
              <div class="col-lg-auto mb-8pt">
                <form class="search-form search-form--light d-lg-inline-flex" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by student name"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon>search</md-icon>
                  </b-btn>
                </form>
              </div>

              <div class="col-lg-auto mb-8pt">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedQuiz"
                  :reduce="quiz => String(quiz.id)"
                  placeholder="Filter by Assessment"
                  :options="quizOptions"
                  :loading="areQuizzesLoading"
                  @input="onQuizFilter"
                  :clearable="true"
                >
                </v-select>
              </div>

              <div class="col-lg-auto mb-8pt">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedCourse"
                  :reduce="course => String(course.id)"
                  placeholder="Filter by Course"
                  :options="courseOptions"
                  :loading="areCoursesLoading"
                  @input="onCourseFilter"
                  :clearable="true"
                />
              </div>

              <div class="col-lg-auto mb-8pt">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedProgram"
                  :reduce="pg => pg.id"
                  placeholder="All Program Categories"
                  :options="allPrograms"
                  :loading="areProgramsLoading"
                  @input="onProgramFilter"
                  :clearable="true"
                />
              </div>

              <div class="col-lg-auto mb-8pt">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 200px;"
                  label="title"
                  v-model="selectedTag"
                  :reduce="t => t.id"
                  placeholder="Filter by Tag"
                  :options="tagsList"
                  :loading="areTagsLoading"
                  @input="onTagFilter"
                  :clearable="true"
                />
              </div>

              <div class="col-lg-auto mb-8pt">
                <v-select
                  class="form-control v-select-custom"
                  style="min-width: 190px;"
                  :reduce="opt => opt.value"
                  label="label"
                  v-model="selectedTypeFilter"
                  placeholder="Filter by Type"
                  :options="typeFilterOptions"
                  @input="onFilterChange"
                  :clearable="true"
                />
              </div>
            </div>

            <b-table
              :fields="tableColumns"
              :items="submissions"
              v-if="submissions.length"
              :busy="isLoading"
              head-variant="light"
              class="table-nowrap"
              hover
              responsive
              @row-clicked="rowClicked"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-2"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>

              <template #cell(student_name)="data">
                <strong>{{ data.item.student_name }}</strong>
              </template>

              <template #cell(quiz_title)="data">
                {{ get(data.item, 'quiz.title', '-') }}
                <b-badge v-if="get(data.item, 'quiz.is_mock_exam')" variant="info" class="ml-1">Mock</b-badge>
              </template>

              <template #cell(course_title)="data">
                {{ data.item.course_title || '-' }}
              </template>

              <template #cell(chapter_title)="data">
                {{ data.item.chapter_title || '-' }}
              </template>

              <template #cell(lesson_title)="data">
                {{ data.item.lesson_title || '-' }}
              </template>

              <template #cell(attempts_count)="data">
                {{ data.item.attempts_count || 0 }}
              </template>

              <template #cell(best_score)="data">
                <span v-if="data.item.best_score !== null">
                  {{ data.item.best_score }}/{{ get(data.item, 'quiz.total_marks', '-') }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(passed)="data">
                <b-badge :variant="data.item.passed ? 'success' : 'danger'">
                  {{ data.item.passed ? 'Passed' : 'Failed' }}
                </b-badge>
              </template>

              <template #cell(last_attempt_at)="data">
                {{ formatDateTime(data.item.last_attempt_at) }}
              </template>

              <template #cell(actions)="data">
                <div class="d-flex justify-content-end">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="view-attempts-btn"
                    :style="viewAttemptsButtonVars"
                    @click.stop.prevent="viewAttempts(data.item)"
                  >
                    View Attempts
                  </b-button>
                </div>
              </template>
            </b-table>

            <div v-if="!isLoading && !submissions.length" class="text-center p-4">
              <p>No assessment submissions found.</p>
            </div>

            <div class="card-footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalSubmissions"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="submissions-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';
import Pagination from '../../../components/Ui/Pagination.vue';
import { formatDateTime } from '../../../common/utils';
import axios from 'axios';

export default {
  components: {
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'Assessment Submissions',
      isLoading: true,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalSubmissions: 0,
      searchTerm: '',
      submissions: [],
      allQuizzes: [],
      areQuizzesLoading: false,
      selectedQuiz: null,
      allCourses: [],
      areCoursesLoading: false,
      selectedCourse: null,
      allPrograms: [],
      areProgramsLoading: false,
      selectedProgram: null,
      tagsList: [],
      areTagsLoading: false,
      selectedTag: null,
      selectedTypeFilter: null,
      typeFilterOptions: [
        { label: 'Graded', value: 'graded' },
        { label: 'Mock Exam', value: 'mock_exam' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    primaryColor() {
      const entity = (this.getLoggedInUser && this.getLoggedInUser.linked_entity) || {};
      return entity.primary_color || '#3b82f6';
    },
    secondaryColor() {
      const entity = (this.getLoggedInUser && this.getLoggedInUser.linked_entity) || {};
      return entity.secondary_color || '#1f2937';
    },
    viewAttemptsButtonVars() {
      return {
        '--launch-btn-color': this.primaryColor || this.secondaryColor || '#3b82f6',
      };
    },
    quizOptions() {
      const optionMap = new Map();

      const addQuiz = quiz => {
        if (!quiz) return;
        const id = get(quiz, 'id');
        const title = get(quiz, 'title');
        if (!id || !title) return;
        const key = String(id);
        if (!optionMap.has(key)) {
          optionMap.set(key, { ...quiz, id: key });
        }
      };

      (Array.isArray(this.allQuizzes) ? this.allQuizzes : []).forEach(addQuiz);
      (Array.isArray(this.submissions) ? this.submissions : []).forEach(submission => addQuiz(get(submission, 'quiz')));

      return Array.from(optionMap.values());
    },
    courseOptions() {
      return (Array.isArray(this.allCourses) ? this.allCourses : [])
        .filter(course => get(course, 'id') && get(course, 'title'))
        .map(course => ({ ...course, id: String(course.id) }));
    },
    tableColumns() {
      return [
        { key: 'student_name', label: 'Student', sortable: false },
        { key: 'quiz_title', label: 'Assessment', sortable: false },
        { key: 'course_title', label: 'Course', sortable: false },
        { key: 'chapter_title', label: 'Chapter', sortable: false },
        { key: 'lesson_title', label: 'Lesson', sortable: false },
        { key: 'attempts_count', label: 'Total Attempts', sortable: false, class: 'text-center' },
        { key: 'best_score', label: 'Best Score', sortable: false, class: 'text-center' },
        { key: 'passed', label: 'Status', sortable: false, class: 'text-center' },
        { key: 'last_attempt_at', label: 'Last Attempt', sortable: false },
        { key: 'actions', label: '', sortable: false, class: 'text-right' },
      ];
    },
  },

  methods: {
    ...mapActions('courses', ['getCourses']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('attendance', ['getTags']),
    get,
    formatDateTime,
    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchSubmissions();
    },

    async fetchQuizzes() {
      this.areQuizzesLoading = true;
      try {
        const res = await axios.get('api/lms/quiz-results/quiz-filter-options/');

        const data = res && res.data ? res.data : [];
        const options = Array.isArray(data)
          ? data
          : Array.isArray(data.results)
          ? data.results
          : Array.isArray(data.data)
          ? data.data
          : [];

        this.allQuizzes = options;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.allQuizzes = [];
      }
      this.areQuizzesLoading = false;
    },

    async fetchCourses() {
      this.areCoursesLoading = true;
      try {
        const data = await this.getCourses({ limit: 100, offset: 0 });
        this.allCourses = Array.isArray(data)
          ? data
          : Array.isArray(data.results)
          ? data.results
          : Array.isArray(data.data)
          ? data.data
          : [];
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.allCourses = [];
      }
      this.areCoursesLoading = false;
    },

    async fetchTags() {
      this.areTagsLoading = true;
      try {
        const res = await this.getTags({
          ...(this.getLoggedInUser.linked_entity && { school__id: this.getLoggedInUser.linked_entity.id }),
        });
        this.tagsList = get(res, 'data.data', []) || [];
      } catch (e) {
        this.tagsList = [];
      }
      this.areTagsLoading = false;
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        const res = await this.getAllPrograms();
        this.allPrograms = res.data;
      } catch (e) {
        this.allPrograms = [];
      }
      this.areProgramsLoading = false;
    },

    async fetchSubmissions() {
      this.isLoading = true;
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.selectedQuiz && { quiz_id: this.selectedQuiz }),
          ...(this.selectedCourse && { course: this.selectedCourse }),
          ...(this.selectedProgram && { program_id: this.selectedProgram }),
          ...(this.selectedTag && { tag_id: this.selectedTag }),
          ...(this.selectedTypeFilter === 'graded' && { is_graded: 'true' }),
          ...(this.selectedTypeFilter === 'mock_exam' && { is_mock_exam: 'true' }),
        };

        const response = await axios.get('api/lms/quiz-results/all/', { params });
        this.submissions = response.data.results ? response.data.results : response.data;
        this.totalSubmissions = response.data.count || response.data.length;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to fetch assessment submissions' });
      } finally {
        this.isLoading = false;
      }
    },

    rowClicked(row) {
      this.viewAttempts(row);
    },

    viewAttempts(submission) {
      const enrollmentId =
        submission.enrollment_id || submission.enrollment || submission.course_enrollment || submission.enrollmentId;
      const quizId = get(submission, 'quiz.id') || submission.quiz_id || submission.quizId;
      const studentId = submission.student || submission.student_id || submission.studentId;

      if (!quizId || !studentId) {
        this.makeToast({ variant: 'warning', msg: 'Missing assessment or student details to view attempts.' });
        return;
      }

      // Build query parameters with student_id and context ID based on quiz_type
      const queryParams = {
        student_id: String(studentId),
      };

      // Add the appropriate context ID based on quiz_type
      const quizType = get(submission, 'quiz.quiz_type') || submission.quiz_type;
      if (quizType === 'lesson') {
        const lessonId = get(submission, 'quiz.lesson_id') || submission.lesson_id || submission.lesson;
        if (lessonId) {
          queryParams.lesson_id = String(lessonId);
        }
      } else if (quizType === 'chapter') {
        const chapterId = get(submission, 'quiz.chapter_id') || submission.chapter_id || submission.chapter;
        if (chapterId) {
          queryParams.chapter_id = String(chapterId);
        }
      } else if (quizType === 'final') {
        const courseId = get(submission, 'quiz.course_id') || submission.course_id || submission.course;
        if (courseId) {
          queryParams.course_id = String(courseId);
        }
      }

      this.$router.push({
        name: 'quiz-attempts',
        params: {
          enrollmentId: String(enrollmentId || 'quiz-submissions'),
          quizId: String(quizId),
        },
        query: queryParams,
      });
    },

    onSearch() {
      this.currentPage = 1;
      this.debouncedSubmissionSearch();
    },

    onQuizFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    onCourseFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    onProgramFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    onTagFilter() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.fetchSubmissions();
    },

    debouncedSubmissionSearch: debounce(function() {
      this.fetchSubmissions();
    }, 500),

    getPerformanceVariant(percent, passingPercent = 50) {
      if (percent === null || percent === undefined) return 'secondary';
      if (percent >= 80) return 'success';
      if (percent >= passingPercent) return 'warning';
      return 'danger';
    },
  },

  async created() {
    await Promise.all([this.fetchQuizzes(), this.fetchCourses(), this.fetchPrograms(), this.fetchTags()]);
    this.fetchSubmissions();
  },
};
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
}

/* Action buttons */
.view-attempts-btn {
  font-weight: 500;
  border-color: var(--launch-btn-color, #3b82f6);
  color: var(--launch-btn-color, #3b82f6);
  background-color: transparent;
  padding: 0.35rem 0.75rem;
  transition: all 0.2s ease;
}

.view-attempts-btn:hover,
.view-attempts-btn:focus {
  background-color: var(--launch-btn-color, #3b82f6);
  color: #ffffff;
}
</style>
